import React, { useMemo, useRef, useState, useEffect } from 'react'
import { makeStyles, IconButton } from '@material-ui/core'
import { useDispatch } from 'react-redux'
import moment from 'moment'
import EditIcon from '@material-ui/icons/CreateOutlined'
import { Modal, Button } from '../../root/components/common'
import CloseIcon from '@material-ui/icons/Close'
import AddressForm from './address-form'
import useUpdateHotswap, { mapDataForUpdate } from '../api/useUpdateHotswap'
import { enqueueSnackbar } from '../../root/actions'
import HotSwapDatePicker from './hot-swap-date-picker'
import HotSwapTimePicker from './hot-swap-time-picker'
import { useQuery } from 'react-query'
import { getHotswapWeeksAvailabilityByLocationIds } from '../api/hotswaps-api'
import { HOTSWAP_TYPES } from '../constants/constants'

const HotSwapSummaryDetails = ({
    formData,
    swapType,
    locale,
    scheduledDate,
    scheduledTime,
    hotswapId,
    isAdmin,
    fetchHotswap
}) => {
    const classes = useStyles()
    const formRef = useRef()
    const parseDate = (date, format) => moment(date).format(format)
    const [isEditAddressModalOpen, setIsEditAddressModalOpen] = useState(false)
    const [isEditDateModalOpen, setIsEditDateModalOpen] = useState(false)
    const [updatedData, setUpdatedData] = useState(formData)
    const [isUpdateReady, setIsUpdateReady] = useState(false)
    const hotswapUpdateMutation = useUpdateHotswap()
    const dispatch = useDispatch()

    const submitAddressForm = async () => {
        await formRef.current.submit()
        setIsUpdateReady(true)
    }

    const onTimeChange = val => {
        setUpdatedData({ ...updatedData, time: val })
    }
    const onDateChange = val => {
        setUpdatedData({ ...updatedData, date: val })
    }

    const { data: disableWeeks } = useQuery([formData.location_id], () =>
        getHotswapWeeksAvailabilityByLocationIds([formData.location_id])
    )

    const editHotswapDetails = () => {
        const mappedData = mapDataForUpdate(updatedData)
        const data = { data: mappedData, hotswapId }
        hotswapUpdateMutation.mutate(data, {
            onSuccess: () => {
                fetchHotswap(hotswapId)
                dispatch(
                    enqueueSnackbar({
                        message: locale.sagasMsgs.deviceImagesEditRes,
                        options: {
                            variant: 'success'
                        }
                    })
                )
            },
            onError: error => {
                dispatch(
                    enqueueSnackbar({
                        message: locale.sagasMsgs.commonLoadingError,
                        options: {
                            variant: 'error'
                        }
                    })
                )
            }
        })
    }

    useEffect(() => {
        if (isUpdateReady) {
            editHotswapDetails()
            setIsUpdateReady(false)
        }
    }, [updatedData])

    const footer = useMemo(() => {
        const { tracking_number, tracking_link, technician_email } = formData
        return (
            <>
                <div className={classes.detailsFooter}>
                    {(tracking_number || technician_email) && (
                        <span className={classes.titleText}>
                            {locale.hotSwap[swapType].footerText}
                        </span>
                    )}
                    {tracking_number && (
                        <span className={classes.labelWrapper}>
                            {tracking_number}
                        </span>
                    )}
                    {technician_email && (
                        <span className={classes.labelWrapper}>
                            {technician_email}
                        </span>
                    )}
                </div>
                {tracking_link && (
                    <div className={classes.detailsFooter}>
                        <span className={classes.titleText}>
                            {locale.hotSwap[swapType].trackingLink}
                        </span>
                        <span className={classes.labelWrapper}>
                            <a
                                target="_blank"
                                href={tracking_link}
                                rel="noreferrer">
                                {tracking_link}
                            </a>
                        </span>
                    </div>
                )}
            </>
        )
    }, [formData])

    return (
        formData && (
            <div className={`${classes.details} ${classes.directionColumn}`}>
                <div className={classes.spaceBetween}>
                    <div className={classes.detailsContainer}>
                        <span
                            className={classes.titleText}
                            style={{ marginBottom: 8 }}>
                            {locale.hotSwap[swapType].summary.date}
                            {isAdmin && (
                                <EditIcon
                                    onClick={() => setIsEditDateModalOpen(true)}
                                    className={classes.editIcon}
                                />
                            )}
                        </span>
                        <span className={classes.regularText}>
                            {parseDate(scheduledDate, 'DD.MM.YYYY')}
                        </span>
                        {scheduledTime && scheduledTime !== '00:00' && (
                            <span className={classes.regularText}>
                                {scheduledTime}
                            </span>
                        )}
                    </div>
                    <div className={classes.detailsContainer}>
                        <span
                            className={classes.titleText}
                            style={{ marginBottom: 8 }}>
                            {locale.hotSwap[swapType].summary.address}
                            {isAdmin && (
                                <EditIcon
                                    onClick={() =>
                                        setIsEditAddressModalOpen(true)
                                    }
                                    className={classes.editIcon}
                                />
                            )}
                        </span>
                        <span className={classes.titleText}>
                            {formData.company}
                        </span>
                        <span className={classes.regularText}>
                            {formData.first_name} {formData.last_name}
                        </span>
                        <span className={classes.regularText}>
                            {formData.street_address}
                        </span>
                        <span className={classes.regularText}>
                            {formData.zip_code} {formData.city}
                        </span>
                        <span className={classes.regularText}>
                            {formData.country}
                        </span>
                    </div>
                </div>
                {footer}

                <Modal
                    open={isEditAddressModalOpen}
                    setOpen={setIsEditAddressModalOpen}>
                    <div className={classes.closeBtn}>
                        <IconButton
                            aria-label="close"
                            onClick={() => setIsEditAddressModalOpen(false)}>
                            <CloseIcon />
                        </IconButton>
                    </div>
                    <div className={classes.modalTitle}>
                        {locale.hotSwap[swapType].summary.address}
                    </div>
                    <AddressForm
                        ref={formRef}
                        setFormData={setUpdatedData}
                        formData={updatedData}
                    />
                    <div className={classes.modalButton}>
                        <Button onClick={() => submitAddressForm()}>
                            {locale.common.save}
                        </Button>
                    </div>
                </Modal>
                <Modal
                    size={
                        formData.hotswap_type === HOTSWAP_TYPES.US ? 'lg' : 'md'
                    }
                    open={isEditDateModalOpen}
                    setOpen={setIsEditDateModalOpen}>
                    <div className={classes.closeBtn}>
                        <IconButton
                            aria-label="close"
                            onClick={() => setIsEditDateModalOpen(false)}>
                            <CloseIcon />
                        </IconButton>
                    </div>
                    <div className={classes.modalTitle}>
                        {locale.hotSwap[swapType].summary.date}
                    </div>
                    <div className={classes.dateTimeContainer}>
                        <HotSwapDatePicker
                            onDateChange={onDateChange}
                            disableWeeks={disableWeeks}
                        />
                        {formData.hotswap_type === HOTSWAP_TYPES.US && (
                            <HotSwapTimePicker onTimeChange={onTimeChange} />
                        )}
                    </div>
                    <div className={classes.modalButton}>
                        <Button onClick={() => editHotswapDetails()}>
                            {locale.common.save}
                        </Button>
                    </div>
                </Modal>
            </div>
        )
    )
}

export default HotSwapSummaryDetails

const useStyles = makeStyles(theme => ({
    detailsContainer: {
        display: 'flex',
        flexDirection: 'column',
        gap: 2
    },
    spaceBetween: {
        display: 'flex',
        justifyContent: 'space-between'
    },
    titleText: {
        fontWeight: 800,
        fontSize: '13px',
        color: theme.blueDark,
        display: 'flex'
    },
    regularText: {
        fontWeight: 300,
        fontSize: '13px',
        color: '#475467',
        lineHeight: '20px'
    },
    details: {
        display: 'flex',
        justifyContent: 'space-between'
    },
    directionColumn: {
        flexDirection: 'column'
    },
    detailsFooter: {
        display: 'flex',
        justifyContent: 'space-between',
        marginTop: '20px',
        alignItems: 'center',
        gap: 8
    },
    labelWrapper: {
        borderRadius: '16px',
        fontWeight: 500,
        fontSize: '12px',
        padding: '4px 12px 4px 12px',
        color: theme.gray,
        backgroundColor: theme.grayLight
    },
    editIcon: {
        fontSize: 16,
        marginLeft: 4,
        cursor: 'pointer'
    },
    modalButton: {
        display: 'flex',
        justifyContent: 'center',
        paddingTop: 24
    },
    modalTitle: {
        color: '#868686',
        display: 'flex',
        justifyContent: 'center',
        fontSize: 22,
        marginBottom: 24
    },
    dateTimeContainer: {
        display: 'flex',
        justifyContent: 'center',
        gap: 24
    },
    closeBtn: {
        display: 'flex',
        justifyContent: 'flex-end',
        width: '100%'
    }
}))
